/**
 * captureStore — projeção efêmera da captura para a interface.
 * No local-first, identidade/fase pertencem ao captureState e só o CaptureRuntime as publica;
 * ações legadas continuam isoladas enquanto os dois fluxos convivem (D1).
 */
import { create } from 'zustand';
import type { ListModule } from '../config/constants';
import type { CapturePauseReason, CapturePhase } from '../lib/captureEngine';
import type {
  ExtensionConnection,
  ExtensionFlow,
  ExtensionObservation,
} from '../lib/extensionFlow';

export interface CaptureState {
  /** Identidade projetada pelo CaptureRuntime; null no fluxo legado ou quando ocioso. */
  runId: string | null;
  /** Fase projetada do IndexedDB; nunca é decidida por este store. */
  phase: CapturePhase | null;
  active: boolean;
  module: ListModule;
  progress: number; // 0..100
  page: number;
  pages: number;
  /** Leads já capturados na lista atual — alimenta o badge de "Ver leads" e habilita "Nova lista". */
  count: number;
  /** Identidade da projeção de count; um total de phones nunca vira badge de emails. */
  countModule: ListModule;
  /** Lista local que pode receber contagem/avanço; lotes de qualquer outra lista são isolados. */
  activeListId: string | null;
  /** Motivo persistível da pausa local-first, exibido pela CapturePage. */
  pauseReason: CapturePauseReason | null;
  /** null = ainda não verificado; true/false = presença da extensão. */
  extensionPresent: boolean | null;
  /** Fluxo declarado pelo código vivo da extensão; null = handshake ainda não concluído. */
  extensionFlow: ExtensionFlow | null;
  /** Estado do canal vivo; `refresh-required` não apaga o último fluxo operacional. */
  extensionConnection: ExtensionConnection;
  /** Popup de categorias aberto (porta de `openCapture`/`showCategories`) — acionado pelo
   * botão "Capturar Leads" do header E pelo card da home. */
  categoriesOpen: boolean;
  /** Janela de captura aberta como OVERLAY sobre a tela atual (porta de `showSearch`:
   * o #window vai por cima do conteúdo — a tela de fundo NÃO é desmontada).
   * null = fechada. */
  captureWindow: ListModule | null;
  /** Abrir esta vez direto na busca completa (botão "Voltar à busca" do cabeçalho). */
  captureFull: boolean;
  /** Abrir esta vez na janela, mesmo para quem usou a tela cheia por último (o guia passo a
   * passo aponta os campos da janela). Não muda a preferência da pessoa. */
  captureJanela: boolean;
  /** Compatibilidade D1: projeção do `sync.play` somente quando não há runtime local. */
  setLegacyActive: (active: boolean) => void;
  setModule: (module: ListModule) => void;
  setLegacyProgress: (progress: number, page?: number, pages?: number) => void;
  setCount: (count: number, module: ListModule) => void;
  setExtensionPresent: (present: boolean) => void;
  setExtensionFlow: (flow: ExtensionFlow) => void;
  setExtensionObservation: (observation: ExtensionObservation) => void;
  openCategories: () => void;
  closeCategories: () => void;
  openCaptureWindow: (module: ListModule, options?: { full?: boolean; janela?: boolean }) => void;
  closeCaptureWindow: () => void;
  reset: () => void;
}

export const useCaptureStore = create<CaptureState>((set) => ({
  runId: null,
  phase: null,
  active: false,
  module: 'emails',
  progress: 0,
  page: 0,
  pages: 0,
  count: 0,
  countModule: 'emails',
  activeListId: null,
  pauseReason: null,
  extensionPresent: null,
  extensionFlow: null,
  extensionConnection: 'checking',
  categoriesOpen: false,
  captureWindow: null,
  captureFull: false,
  captureJanela: false,
  setLegacyActive: (active) => set((state) => (state.runId ? state : { active })),
  setModule: (module) => set((state) => (state.runId ? state : { module })),
  setLegacyProgress: (progress, page, pages) =>
    set((state) =>
      state.runId ? state : { progress, page: page ?? state.page, pages: pages ?? state.pages },
    ),
  setCount: (count, countModule) => set({ count, countModule }),
  setExtensionPresent: (present) =>
    set({ extensionPresent: present, extensionConnection: present ? 'connected' : 'unavailable' }),
  setExtensionFlow: (extensionFlow) =>
    set({
      extensionFlow,
      extensionPresent: extensionFlow !== 'unavailable',
      extensionConnection: extensionFlow === 'unavailable' ? 'unavailable' : 'connected',
    }),
  setExtensionObservation: ({ flow: extensionFlow, connection: extensionConnection }) =>
    set({
      extensionFlow,
      extensionConnection,
      extensionPresent:
        extensionConnection === 'connected'
          ? true
          : extensionConnection === 'unavailable'
            ? false
            : null,
    }),
  openCategories: () => set({ categoriesOpen: true }),
  closeCategories: () => set({ categoriesOpen: false }),
  // O Hunter saiu do painel (17/10/2026): nenhum caminho abre a captura dele.
  openCaptureWindow: (module, options) =>
    set((state) =>
      module === 'hunter'
        ? {}
        : {
            captureWindow: module,
            captureFull: Boolean(options?.full),
            captureJanela: Boolean(options?.janela),
            ...(state.runId ? {} : { module }),
          },
    ),
  closeCaptureWindow: () => set({ captureWindow: null, captureFull: false, captureJanela: false }),
  reset: () =>
    set({
      runId: null,
      phase: null,
      active: false,
      progress: 0,
      page: 0,
      pages: 0,
      count: 0,
      countModule: 'emails',
      activeListId: null,
      pauseReason: null,
    }),
}));
